import { authenticatedFetch } from '../utils/authenticatedFetch';
import { configs } from '../configs';

export interface LeaveRequest {
  id: string;
  staffId: string;
  staffName: string;
  startDate: string;
  endDate: string;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  leaveType: 'paid' | 'unpaid' | 'sick' | 'other';
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}

export type LeaveRequestCreateEntry = Omit<LeaveRequest, 'id' | 'createdAt' | 'updatedAt' | 'tenantId'>;
export type LeaveRequestUpdateEntry = Partial<LeaveRequest>;


class LeaveRequestService {
  private apiRoot = configs.apiRoot;

  async getLeaveRequests(): Promise<LeaveRequest[]> {
    const response = await authenticatedFetch(`${this.apiRoot}/leaveRequests`, {
      method: 'GET',
    });
    
    if (!response.ok) {
       const error = await response.json();
       throw new Error(error.message || `Failed to fetch leave requests: ${response.statusText}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : data.data || [];
  }

  async getLeaveRequestsByStaff(staffId: string): Promise<LeaveRequest[]> {
    const response = await authenticatedFetch(`${this.apiRoot}/leaveRequests?staffId=${staffId}`, {
      method: 'GET',
    });

    if (!response.ok) {
       const error = await response.json();
       throw new Error(error.message || `Failed to fetch staff leave requests: ${response.statusText}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : data.data || [];
  }

  async createLeaveRequest(entry: LeaveRequestCreateEntry): Promise<LeaveRequest> {
    const response = await authenticatedFetch(`${this.apiRoot}/leaveRequests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(entry),
    });

    if (!response.ok) {
       const error = await response.json();
       throw new Error(error.message || `Failed to create leave request: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data || result;
  }

  async updateLeaveRequest(id: string, entry: LeaveRequestUpdateEntry): Promise<LeaveRequest> {
    const response = await authenticatedFetch(`${this.apiRoot}/leaveRequests/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(entry),
    });

    if (!response.ok) {
       const error = await response.json();
       throw new Error(error.message || `Failed to update leave request: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data || result;
  }

  async deleteLeaveRequest(id: string): Promise<void> {
    const response = await authenticatedFetch(`${this.apiRoot}/leaveRequests/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
       const error = await response.json();
       throw new Error(error.message || `Failed to delete leave request: ${response.statusText}`);
    }
  }
}

export const leaveRequestService = new LeaveRequestService();

